import { useCallback, useEffect, useMemo, useRef, useState, type FormEvent } from "react";
import { useTranslation } from "react-i18next";

import { ApiError, API_BASE_URL } from "../../../services/apiClient";
import { useAuth } from "../../../providers/AuthProvider";
import { PageShell } from "../../shared/layouts/PageShell";
import { SurfaceCard } from "../../shared/ui/surface/SurfaceCard";
import { InfoBanner } from "../../../components/InfoBanner";
import { Breadcrumbs } from "../../../components/Breadcrumbs";
import type { BreadcrumbItem } from "../../../components/Breadcrumbs";
import { useBreadcrumbBack } from "../../../components/useBreadcrumbBack";
import { PageHeader } from "../../shared/ui/layouts/PageHeader";
import { UnreadBadge } from "../../shared/ui/UnreadBadge";
import { ConnectionStatusPill } from "../../messaging/components/ConnectionStatusPill";
import { MessageBubble } from "../../messaging/components/MessageBubble";
import { MessageComposer } from "../../messaging/components/MessageComposer";
import { Avatar } from "../../shared/ui/Avatar";
import {
  fetchSupportConversationsAdmin,
  fetchSupportMessages,
  markSupportRead,
  sendSupportMessage,
  type SupportConversation,
  type SupportMessage,
} from "../api/support";
import { applyReadReceipt, getLatestMessage, upsertMessages, type SupportMessageUpdate } from "../../messaging/utils/messages";
import { createLogger } from "../../../utils/logger";
import { validateMessageContent } from "../../messaging/utils/sanitize";
import {
  ArrowLeft,
  ChatCircleDots,
} from "@phosphor-icons/react";
import { defaultIconProps } from "../../shared/ui/icons";

type ConnectionState = "connecting" | "open" | "closed";

export function AdminSupportInboxPage() {
  const { t } = useTranslation();
  const { accessToken, user } = useAuth();
  const logger = useMemo(() => createLogger("SupportAdmin"), []);
  const breadcrumbItems = useMemo<BreadcrumbItem[]>(
    () => [
      { label: t("admin.title"), to: "/admin" },
      { label: t("support.admin.title", "Customer service inbox") },
    ],
    [t],
  );
  const handleBack = useBreadcrumbBack({
    items: breadcrumbItems,
    includeHome: true,
    fallback: { to: "/admin" },
  });
  const [conversations, setConversations] = useState<SupportConversation[]>([]);
  const [totalUnread, setTotalUnread] = useState<number>(0);
  const [selectedConversation, setSelectedConversation] =
    useState<SupportConversation | null>(null);
  const [messages, setMessages] = useState<SupportMessage[]>([]);
  const [inputValue, setInputValue] = useState("");
  const [isSending, setIsSending] = useState(false);
  const [isLoadingMessages, setIsLoadingMessages] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [connectionState, setConnectionState] =
    useState<ConnectionState>("closed");
  const scrollerRef = useRef<HTMLDivElement | null>(null);
  const socketRef = useRef<WebSocket | null>(null);
  const refreshMessagesRef = useRef<((conversationId: string) => Promise<void>) | null>(null);

  const markLatestAsRead = useCallback(
    async (token: string, conversationId: string, latestMessages: SupportMessage[]) => {
      const newest = getLatestMessage(latestMessages);
      if (!newest) return;
      try {
        await markSupportRead(token, conversationId, newest.id);
        setConversations((prev) => {
          let total = 0;
          const next = prev.map((conv) => {
            const count = conv.unread_count ?? 0;
            if (conv.id === conversationId) {
              return { ...conv, unread_count: 0 };
            }
            total += count;
            return conv;
          });
          setTotalUnread(total);
          return next;
        });
      } catch (readError) {
        logger.warn("Failed to mark support conversation as read (admin)", readError);
      }
    },
    [logger]
  );

  useEffect(() => {
    if (!scrollerRef.current) return;
    scrollerRef.current.scrollTo({
      top: scrollerRef.current.scrollHeight,
      behavior: "smooth",
    });
  }, [messages.length]);

  const connectWebSocket = useCallback(
    (token: string, conversationId: string) => {
      socketRef.current?.close();
      const wsUrl = buildWebSocketUrl(
        `/support/conversations/${conversationId}/ws?token=${encodeURIComponent(token)}`,
      );
      const socket = new WebSocket(wsUrl);
      socketRef.current = socket;
      setConnectionState("connecting");
      socket.onopen = () => setConnectionState("open");
      socket.onclose = () => setConnectionState("closed");
      socket.onerror = () => setConnectionState("closed");
      socket.onmessage = async (event) => {
        try {
          const payload = JSON.parse(event.data);
          if (payload?.type === "message.created" && payload.message) {
            const incoming = payload.message as SupportMessage;
            setMessages((prev) => upsertMessages(prev, [incoming]));
            await markLatestAsRead(token, conversationId, [incoming]);
            if (incoming.created_at) {
              setConversations((prev) =>
                prev.map((conv) =>
                  conv.id === conversationId
                    ? { ...conv, last_message_at: incoming.created_at }
                    : conv,
                ),
              );
            }
          }
          if (payload?.type === "message.updated" && Array.isArray(payload.messages)) {
            setMessages((prev) =>
              upsertMessages(prev, payload.messages as SupportMessageUpdate[]),
            );
          }
          if (payload?.type === "read.receipt" && payload.readReceipt && user) {
            const receiptConversation = payload.readReceipt.conversation_id;
            if (receiptConversation && receiptConversation === conversationId) {
              setMessages((prev) => applyReadReceipt(prev, payload.readReceipt, user.id));
              const refresh = refreshMessagesRef.current;
              if (refresh) {
                void refresh(conversationId);
              }
            }
          }
        } catch (parseError) {
          logger.warn("Failed to parse websocket payload", parseError);
        }
      };
    },
    [logger, markLatestAsRead, user],
  );

  const selectConversation = useCallback(
    async (conversation: SupportConversation) => {
      if (!accessToken) {
        setError(t("support.admin.load_messages_error", "Unable to load messages."));
        return;
      }
      const token = accessToken;
      setSelectedConversation(conversation);
      setMessages([]);
      socketRef.current?.close();
      setConnectionState("connecting");
      setIsLoadingMessages(true);
      try {
        const res = await fetchSupportMessages(token, conversation.id, undefined, 50);
        const sortedMessages = upsertMessages([], res.messages);
        setMessages(sortedMessages);
        await markLatestAsRead(token, conversation.id, sortedMessages);
        const latest = getLatestMessage(sortedMessages);
        if (latest?.created_at) {
          setConversations((prev) =>
            prev.map((conv) =>
              conv.id === conversation.id ? { ...conv, last_message_at: latest.created_at } : conv,
            ),
          );
        }
        connectWebSocket(token, conversation.id);
      } catch (err) {
        const apiError = err as ApiError;
        setError(
          apiError?.body?.message ||
          t("support.admin.load_messages_error", "Unable to load messages."),
        );
        setConnectionState("closed");
      } finally {
        setIsLoadingMessages(false);
      }
    },
    [accessToken, connectWebSocket, markLatestAsRead, t],
  );

  refreshMessagesRef.current = useCallback(
    async (conversationId: string) => {
      if (!accessToken) return;
      try {
        const res = await fetchSupportMessages(accessToken, conversationId, undefined, 50);
        const sorted = upsertMessages([], res.messages);
        setMessages(sorted);
        const latest = getLatestMessage(sorted);
        if (latest?.created_at) {
          setConversations((prev) =>
            prev.map((conv) =>
              conv.id === conversationId ? { ...conv, last_message_at: latest.created_at } : conv,
            ),
          );
        }
      } catch (err) {
        logger.warn("Failed to refresh messages after receipt", err);
      }
    },
    [accessToken, logger],
  );

  useEffect(() => {
    if (!accessToken) {
      setError(t("support.admin.load_error", "Unable to load conversations."));
      return;
    }
    const token = accessToken;
    let cancelled = false;
    async function loadConversations() {
      setError(null);
      try {
        const res = await fetchSupportConversationsAdmin(token);
        if (cancelled) return;
        setConversations(res.conversations);
        const unreadTotal =
          res.total_unread ??
          res.conversations.reduce((sum, conv) => sum + (conv.unread_count ?? 0), 0);
        setTotalUnread(unreadTotal);
        if (res.conversations.length > 0) {
          void selectConversation(res.conversations[0]);
        }
      } catch (err) {
        const apiError = err as ApiError;
        setError(
          apiError?.body?.message ||
            t("support.admin.load_error", "Unable to load conversations."),
        );
      }
    }
    loadConversations();
    return () => {
      cancelled = true;
    };
  }, [accessToken, t, selectConversation]);

  const handleSend = async (event?: FormEvent) => {
    if (event) {
      event.preventDefault();
    }
    if (!accessToken || !selectedConversation) return;
    const { content, errorKey } = validateMessageContent(inputValue);
    if (errorKey) {
      setError(t(errorKey));
      setInputValue(content);
      return;
    }
    if (!content) return;
    const token = accessToken;
    setIsSending(true);
    try {
      const message = await sendSupportMessage(
        token,
        selectedConversation.id,
        content,
      );
      setMessages((prev) => upsertMessages(prev, [message]));
      setInputValue("");
      await markLatestAsRead(token, selectedConversation.id, [message]);
    } catch (err) {
      const apiError = err as ApiError;
      setError(
        apiError?.body?.message ||
          t("support.admin.send_error", "Unable to send message."),
      );
    } finally {
      setIsSending(false);
    }
  };

  return (
    <PageShell width="wide">
      <div className="flex flex-col gap-4">
        <Breadcrumbs items={breadcrumbItems} />
        <PageHeader
          title={t("support.admin.title", "Customer service inbox")}
          subtitle={t(
            "support.admin.subtitle",
            "Respond to customer messages and keep conversations organized.",
          )}
          actions={
            <div className="flex flex-wrap items-center gap-3">
              <button
                type="button"
                className="pm-btn pm-btn-ghost pm-btn-md"
                onClick={handleBack}
              >
                <ArrowLeft {...defaultIconProps} aria-hidden />
                {t("actions.back", "Back")}
              </button>
              <UnreadBadge
                count={totalUnread}
                size="md"
                aria-label={t("support.admin.total_unread", "Unread")}
              />
            </div>
          }
        />
        {error ? (
          <InfoBanner
            tone="info"
            title={t("support.admin.load_error", "Unable to load conversations.")}
            description={error}
          />
        ) : null}
        <div className="grid gap-4 lg:grid-cols-[320px_minmax(0,1fr)]">
          <SurfaceCard className="pm-scrollbar h-[640px] overflow-y-auto bg-white/90 p-3 shadow-sm ring-1 ring-slate-200 dark:bg-slate-900/70 dark:ring-slate-800">
            <p className="px-2 pb-2 text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
              {t("support.admin.conversations", "Conversations")}
            </p>
            <div className="flex flex-col gap-2">
              {conversations.length === 0 ? (
                <div className="flex h-48 flex-col items-center justify-center gap-2 text-center text-sm text-slate-500 dark:text-slate-300">
                  <ChatCircleDots size={24} aria-hidden />
                  {t("support.admin.empty", "No conversations yet.")}
                </div>
              ) : (
                conversations.map((conversation) => {
                  const isActive = conversation.id === selectedConversation?.id;
                  const participant =
                    conversation.participants.find((p) => p.role === "customer") ||
                    conversation.participants[0];
                  const participantName =
                    participant?.name ||
                    participant?.email ||
                    t("support.customer_label", "Customer");
                  const unread = conversation.unread_count ?? 0;
                  return (
                    <button
                      key={conversation.id}
                      onClick={() => void selectConversation(conversation)}
                      className={`w-full rounded-xl border px-3 py-3 text-left transition ${
                        isActive
                          ? "border-sky-500 bg-sky-50 text-slate-900 shadow-sm dark:border-sky-400 dark:bg-sky-900/30 dark:text-slate-50"
                          : "border-slate-200 bg-white hover:border-slate-300 dark:border-slate-800 dark:bg-slate-900 dark:hover:border-slate-700"
                      }`}
                    >
                      <div className="flex items-center gap-2">
                        <Avatar
                          size={40}
                          name={participant?.name}
                          email={participant?.email}
                          photoUrl={participant?.profile_photo?.download_url ?? null}
                          fallbackUrl={participant?.profile_photo?.remote_url ?? null}
                          className="border border-slate-200 shadow-sm dark:border-slate-700"
                        />
                        <div className="flex flex-col">
                          <span className="text-sm font-semibold">
                            {participantName}
                          </span>
                          <div className="flex items-center gap-2">
                            <span className="text-xs text-slate-500 dark:text-slate-400">
                              {conversation.last_message_at
                                ? new Date(conversation.last_message_at).toLocaleString()
                                : t("support.admin.no_messages", "No messages yet")}
                            </span>
                            <UnreadBadge count={unread} />
                          </div>
                        </div>
                      </div>
                    </button>
                  );
                })
              )}
            </div>
          </SurfaceCard>
          <SurfaceCard className="flex min-h-[640px] flex-col gap-3 bg-white/90 p-4 shadow-sm ring-1 ring-slate-200 dark:bg-slate-900/70 dark:ring-slate-800">
            <div className="flex items-center justify-between gap-3">
              <div>
                <p className="text-sm font-semibold text-slate-900 dark:text-slate-50">
                  {selectedConversation
                    ? t("support.admin.thread_title", "Conversation details")
                    : t("support.admin.select_prompt", "Select a conversation")}
                </p>
                <p className="text-xs text-slate-500 dark:text-slate-400">
                  {selectedConversation
                    ? selectedConversation.participants
                        .map((p) => p.name || p.email)
                        .filter(Boolean)
                        .join(" · ")
                    : t("support.admin.select_hint", "Choose a conversation to respond.")}
                </p>
              </div>
              {selectedConversation ? (
                <ConnectionStatusPill
                  state={connectionState}
                  label={t(`support.connection.${connectionState}`)}
                />
              ) : null}
            </div>

            <div
              ref={scrollerRef}
              className="pm-scrollbar flex-1 overflow-y-auto rounded-xl bg-slate-50/80 px-3 py-4 dark:bg-slate-800/60"
            >
              {!selectedConversation ? (
                <div className="flex h-full items-center justify-center text-sm text-slate-500 dark:text-slate-300">
                  {t("support.admin.no_selection", "Pick a conversation to view messages.")}
                </div>
              ) : isLoadingMessages ? (
                <div className="flex h-full items-center justify-center">
                  <InlineSpinner />
                </div>
              ) : messages.length === 0 ? (
                <div className="flex h-full flex-col items-center justify-center gap-2 text-sm text-slate-500 dark:text-slate-300">
                  <ChatCircleDots size={24} aria-hidden />
                  {t("support.admin.no_messages", "No messages yet")}
                </div>
              ) : (
                <div className="flex flex-col gap-3">
                  {messages.map((message) => {
                    const isSupport = message.sender_role === "support";
                    const statusLabel = isSupport
                      ? t(`support.delivery.${message.delivery_state}`, message.delivery_state)
                      : undefined;
                    return (
                      <MessageBubble
                        key={message.id}
                        content={message.content}
                        timestamp={message.created_at}
                        isOwn={isSupport}
                        deliveryState={
                          isSupport
                            ? (message.delivery_state as "pending" | "sent" | "delivered" | "read")
                            : undefined
                        }
                        deliveryLabel={statusLabel}
                      />
                    );
                  })}
                </div>
              )}
            </div>

            {selectedConversation ? (
              <MessageComposer
                value={inputValue}
                onChange={setInputValue}
                onSend={() => void handleSend()}
                placeholder={t("support.admin.message_placeholder", "Type your reply...")}
                label={t("support.message_input.label", "Message support")}
                sendLabel={t("support.send", "Send")}
                inputId="support-admin-message"
                isSending={isSending}
                disabled={!selectedConversation}
              />
            ) : null}
          </SurfaceCard>
        </div>
      </div>
    </PageShell>
  );
}

function buildWebSocketUrl(path: string) {
  const url = new URL(API_BASE_URL);
  url.protocol = url.protocol === "https:" ? "wss:" : "ws:";
  return `${url.origin}${url.pathname}${path}`;
}

function InlineSpinner() {
  return (
    <span
      className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-slate-300 border-t-slate-600 dark:border-slate-700 dark:border-t-slate-200"
      aria-hidden
    />
  );
}
